import { z } from "zod";

// ── Constants ──────────────────────────────────────────────────────────────────

export const FEEDBACK_FIELD_TYPES = ["text", "number", "select", "url"] as const;
export type FeedbackFieldType = (typeof FEEDBACK_FIELD_TYPES)[number];

export const FEEDBACK_SORT_OPTIONS = ["votes", "newest", "trending"] as const;
export type FeedbackSortOption = (typeof FEEDBACK_SORT_OPTIONS)[number];

export const FEEDBACK_TAG_AUTO_ACTIONS = ["archive", "close", "lock", "private"] as const;
export type FeedbackTagAutoAction = (typeof FEEDBACK_TAG_AUTO_ACTIONS)[number];

export const FEEDBACK_BOARD_PERMISSION_LEVELS = ["moderator", "staff"] as const;
export type FeedbackBoardPermissionLevel = (typeof FEEDBACK_BOARD_PERMISSION_LEVELS)[number];

export const FEEDBACK_VOTE_TYPES = ["up", "down"] as const;
export type FeedbackVoteType = (typeof FEEDBACK_VOTE_TYPES)[number];

export const FEEDBACK_DEFAULT_STATUSES = [
  { name: "Under Review", color: "#6B7280", isDefault: true, isClosed: false, displayOrder: 0 },
  { name: "Planned", color: "#3B82F6", isDefault: false, isClosed: false, displayOrder: 1 },
  { name: "In Progress", color: "#F59E0B", isDefault: false, isClosed: false, displayOrder: 2 },
  { name: "Complete", color: "#10B981", isDefault: false, isClosed: true, displayOrder: 3 },
  { name: "Won't Do", color: "#EF4444", isDefault: false, isClosed: true, displayOrder: 4 },
] as const;

// ── Response Schemas ───────────────────────────────────────────────────────────

export const FeedbackStatusSchema = z.object({
  id: z.number().int(),
  boardId: z.number().int(),
  name: z.string(),
  color: z.string(),
  displayOrder: z.number().int(),
  isDefault: z.boolean(),
  isClosed: z.boolean(),
  createdAt: z.string(),
});
export type FeedbackStatus = z.infer<typeof FeedbackStatusSchema>;

export const FeedbackTagSchema = z.object({
  id: z.number().int(),
  boardId: z.number().int(),
  name: z.string(),
  color: z.string(),
  autoAction: z.enum(FEEDBACK_TAG_AUTO_ACTIONS).nullable(),
  createdAt: z.string(),
});
export type FeedbackTag = z.infer<typeof FeedbackTagSchema>;

export const FeedbackCustomFieldSchema = z.object({
  id: z.number().int(),
  boardId: z.number().int(),
  name: z.string(),
  fieldType: z.enum(FEEDBACK_FIELD_TYPES),
  options: z.array(z.string()).nullable(),
  isRequired: z.boolean(),
  displayOrder: z.number().int(),
  createdAt: z.string(),
});
export type FeedbackCustomField = z.infer<typeof FeedbackCustomFieldSchema>;

export const FeedbackBoardPermissionSchema = z.object({
  id: z.number().int(),
  boardId: z.number().int(),
  discordRoleId: z.string(),
  discordRoleName: z.string(),
  permissionLevel: z.enum(FEEDBACK_BOARD_PERMISSION_LEVELS),
  createdAt: z.string(),
});
export type FeedbackBoardPermission = z.infer<typeof FeedbackBoardPermissionSchema>;

export const FeedbackBoardSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  coverImage: z.string().nullable(),
  isPrivate: z.boolean(),
  isArchived: z.boolean(),
  displayOrder: z.number().int(),
  postCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeedbackBoard = z.infer<typeof FeedbackBoardSchema>;

export const FeedbackBoardDetailSchema = FeedbackBoardSchema.extend({
  statuses: z.array(FeedbackStatusSchema),
  tags: z.array(FeedbackTagSchema),
  fields: z.array(FeedbackCustomFieldSchema),
  permissions: z.array(FeedbackBoardPermissionSchema),
});
export type FeedbackBoardDetail = z.infer<typeof FeedbackBoardDetailSchema>;

export const FeedbackFieldValueSchema = z.object({
  id: z.number().int(),
  postId: z.number().int(),
  fieldId: z.number().int(),
  value: z.string(),
  field: FeedbackCustomFieldSchema.nullable().optional(),
});
export type FeedbackFieldValue = z.infer<typeof FeedbackFieldValueSchema>;

export const FeedbackCommentReactionSchema = z.object({
  emoji: z.string(),
  count: z.number().int(),
  hasReacted: z.boolean(),
});
export type FeedbackCommentReaction = z.infer<typeof FeedbackCommentReactionSchema>;

export const FeedbackPostSchema = z.object({
  id: z.number().int(),
  boardId: z.number().int(),
  statusId: z.number().int().nullable(),
  title: z.string(),
  description: z.string(),
  authorId: z.number().int().nullable(),
  authorName: z.string().nullable(),
  authorAvatar: z.string().nullable(),
  isPrivate: z.boolean(),
  isPinned: z.boolean(),
  isLocked: z.boolean(),
  mergedIntoId: z.number().int().nullable(),
  voteCount: z.number().int(),
  downvoteCount: z.number().int(),
  hasVoted: z.boolean(),
  voteType: z.enum(FEEDBACK_VOTE_TYPES).nullable(),
  isSubscribed: z.boolean().optional(),
  tags: z.array(FeedbackTagSchema),
  commentCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeedbackPost = z.infer<typeof FeedbackPostSchema>;

export const FeedbackCommentSchema = z.object({
  id: z.number().int(),
  postId: z.number().int(),
  authorId: z.number().int().nullable(),
  authorName: z.string().nullable(),
  authorAvatar: z.string().nullable(),
  body: z.string(),
  isInternal: z.boolean(),
  reactions: z.array(FeedbackCommentReactionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeedbackComment = z.infer<typeof FeedbackCommentSchema>;

export const FeedbackPostUpdateSchema = z.object({
  id: z.number().int(),
  postId: z.number().int(),
  authorId: z.number().int().nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});
export type FeedbackPostUpdate = z.infer<typeof FeedbackPostUpdateSchema>;

export const FeedbackMergedPostSlimSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  boardSlug: z.string(),
});
export type FeedbackMergedPostSlim = z.infer<typeof FeedbackMergedPostSlimSchema>;

export const FeedbackPostDetailSchema = FeedbackPostSchema.extend({
  status: FeedbackStatusSchema.nullable(),
  fieldValues: z.array(FeedbackFieldValueSchema),
  comments: z.array(FeedbackCommentSchema),
  updates: z.array(FeedbackPostUpdateSchema),
  mergedPost: FeedbackMergedPostSlimSchema.nullable(),
  isSubscribed: z.boolean(),
});
export type FeedbackPostDetail = z.infer<typeof FeedbackPostDetailSchema>;

// ── Body Schemas ───────────────────────────────────────────────────────────────

const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex color (#RRGGBB)");

const feedbackSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only");

export const CreateBoardBodySchema = z.object({
  name: z.string().min(1).max(128),
  slug: feedbackSlugSchema,
  description: z.string().max(500).nullish(),
  coverImage: z.string().url().max(500).nullish(),
  isPrivate: z.boolean().optional(),
});
export type CreateBoardBody = z.infer<typeof CreateBoardBodySchema>;

export const PatchBoardBodySchema = z
  .object({
    name: z.string().min(1).max(128).optional(),
    slug: feedbackSlugSchema.optional(),
    description: z.string().max(500).nullable().optional(),
    coverImage: z.string().url().max(500).nullable().optional(),
    isPrivate: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    displayOrder: z.number().int().min(0).optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: "At least one field is required",
  });
export type PatchBoardBody = z.infer<typeof PatchBoardBodySchema>;

export const CreateStatusBodySchema = z.object({
  name: z.string().min(1).max(64),
  color: hexColorSchema.optional().default("#6B7280"),
  displayOrder: z.number().int().min(0).optional(),
  isDefault: z.boolean().optional().default(false),
  isClosed: z.boolean().optional().default(false),
});
export type CreateStatusBody = z.infer<typeof CreateStatusBodySchema>;

export const PatchStatusBodySchema = z
  .object({
    name: z.string().min(1).max(64).optional(),
    color: hexColorSchema.optional(),
    displayOrder: z.number().int().min(0).optional(),
    isDefault: z.boolean().optional(),
    isClosed: z.boolean().optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: "At least one field is required",
  });
export type PatchStatusBody = z.infer<typeof PatchStatusBodySchema>;

export const ReorderStatusesBodySchema = z.object({
  orderedIds: z.array(z.number().int().positive()).min(1),
});
export type ReorderStatusesBody = z.infer<typeof ReorderStatusesBodySchema>;

export const CreateTagBodySchema = z.object({
  name: z.string().min(1).max(64),
  color: hexColorSchema.optional().default("#6B7280"),
  autoAction: z.enum(FEEDBACK_TAG_AUTO_ACTIONS).nullable().optional(),
});
export type CreateTagBody = z.infer<typeof CreateTagBodySchema>;

export const PatchTagBodySchema = z
  .object({
    name: z.string().min(1).max(64).optional(),
    color: hexColorSchema.optional(),
    autoAction: z.enum(FEEDBACK_TAG_AUTO_ACTIONS).nullable().optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: "At least one field is required",
  });
export type PatchTagBody = z.infer<typeof PatchTagBodySchema>;

export const CreateFieldBodySchema = z.object({
  name: z.string().min(1).max(128),
  fieldType: z.enum(FEEDBACK_FIELD_TYPES),
  options: z.array(z.string().min(1).max(128)).nullable().optional(),
  isRequired: z.boolean().optional().default(false),
  displayOrder: z.number().int().min(0).optional(),
});
export type CreateFieldBody = z.infer<typeof CreateFieldBodySchema>;

export const PatchFieldBodySchema = z
  .object({
    name: z.string().min(1).max(128).optional(),
    fieldType: z.enum(FEEDBACK_FIELD_TYPES).optional(),
    options: z.array(z.string().min(1).max(128)).nullable().optional(),
    isRequired: z.boolean().optional(),
    displayOrder: z.number().int().min(0).optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: "At least one field is required",
  });
export type PatchFieldBody = z.infer<typeof PatchFieldBodySchema>;

export const FeedbackFieldValueInputSchema = z.object({
  fieldId: z.number().int().positive(),
  value: z.string().max(2000),
});

export const CreatePostBodySchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().min(1).max(50000),
  isPrivate: z.boolean().optional(),
  fieldValues: z.array(FeedbackFieldValueInputSchema).optional().default([]),
});
export type CreatePostBody = z.infer<typeof CreatePostBodySchema>;

export const PatchPostBodySchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().min(1).max(50000).optional(),
    isPrivate: z.boolean().optional(),
    isPinned: z.boolean().optional(),
    isLocked: z.boolean().optional(),
    statusId: z.number().int().positive().nullable().optional(),
    fieldValues: z.array(FeedbackFieldValueInputSchema).optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: "At least one field is required",
  });
export type PatchPostBody = z.infer<typeof PatchPostBodySchema>;

export const MergePostBodySchema = z.object({
  targetPostId: z.number().int().positive(),
});
export type MergePostBody = z.infer<typeof MergePostBodySchema>;

export const ToggleVoteBodySchema = z.object({
  voteType: z.enum(FEEDBACK_VOTE_TYPES).optional().default("up"),
});
export type ToggleVoteBody = z.infer<typeof ToggleVoteBodySchema>;

export const CreateCommentBodySchema = z.object({
  body: z.string().min(1).max(10000),
  isInternal: z.boolean().optional().default(false),
});
export type CreateCommentBody = z.infer<typeof CreateCommentBodySchema>;

export const PatchCommentBodySchema = z.object({
  body: z.string().min(1).max(10000),
});
export type PatchCommentBody = z.infer<typeof PatchCommentBodySchema>;

export const CreatePostUpdateBodySchema = z.object({
  body: z.string().min(1).max(10000),
});
export type CreatePostUpdateBody = z.infer<typeof CreatePostUpdateBodySchema>;

export const CreateBoardPermissionBodySchema = z.object({
  discordRoleId: z.string().min(1).max(30),
  discordRoleName: z.string().min(1).max(128),
  permissionLevel: z.enum(FEEDBACK_BOARD_PERMISSION_LEVELS),
});
export type CreateBoardPermissionBody = z.infer<typeof CreateBoardPermissionBodySchema>;

export const ToggleReactionBodySchema = z.object({
  emoji: z.string().min(1).max(32),
});
export type ToggleReactionBody = z.infer<typeof ToggleReactionBodySchema>;

export const ListPostsQuerySchema = z.object({
  statusId: z.coerce.number().int().positive().optional(),
  tagId: z.coerce.number().int().positive().optional(),
  sort: z.enum(FEEDBACK_SORT_OPTIONS).optional().default("votes"),
  q: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
  includePrivate: z
    .string()
    .optional()
    .transform((v) => v === "true"),
});
export type ListPostsQuery = z.infer<typeof ListPostsQuerySchema>;
